import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import { useParams } from 'src/core/common/hooks/useParams';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { useBudgetQuery } from '../../../../containers/BudgetOverviewPageContainer/hooks/useBudgetQuery';
import { useIsUserAllowedToAccessBudgets } from '../../../../containers/BudgetOverviewPageContainer/hooks/useIsUserAllowedToAccessBudgets';
import { usePageDataQuery } from '../../../../containers/BudgetaryExercisesPageContainer/hooks/usePageDataQuery';
import { useExpenseCategoriesBreakdownQuery } from '../../../../hooks/useExpenseCategoriesBreakdownQuery';
import { type PeriodRange } from '../../../../models/periodRange';
import { BudgetOverviewPageContent } from '../BudgetOverviewPageContent';

export const NewBudgetOverviewPage = () => {
  const { id: budgetId } = useParams(routes.BUDGET_OVERVIEW.path);
  const { subBudgetId: expenseCategoryId } = useParams(
    routes.SUB_BUDGET_OVERVIEW.path,
  );
  const location = useLocation();
  const history = useHistory();
  const companyId = useCompanyId();
  const [selectedPeriodRange, setSelectedPeriodRange] = useState<PeriodRange>(
    location.state?.selectedPeriodRange ?? {
      key: 'overall',
    },
  );
  const queryStates = useQueryStates({
    states: {
      pageData: usePageDataQuery(),
      budget: useBudgetQuery(budgetId),
      isUserAllowedToAccessBudgets: useIsUserAllowedToAccessBudgets(budgetId),
      expenseCategoriesBreakdown: useExpenseCategoriesBreakdownQuery(budgetId, {
        from: undefined,
        to: undefined,
      }),
    },
  });
  const isUserAllowedToAccessBudgets =
    useIsUserAllowedToAccessBudgets(budgetId);

  useEffect(() => {
    if (
      isUserAllowedToAccessBudgets.status === 'success' &&
      isUserAllowedToAccessBudgets.data === false
    ) {
      history.replace(routeFor(routes.HOMEPAGE.path, { company: companyId }));
    }
  }, [isUserAllowedToAccessBudgets]);

  return (
    <ErrorBoundary context={{ scope: 'budget-overview', team: 'control' }}>
      <QuerySuspense
        queryState={queryStates}
        fallback={(error) => (
          <QueryError queryError={error} componentType="Callout" />
        )}
        loading={null}
      >
        {({ pageData, budget, expenseCategoriesBreakdown }) => {
          const budgetaryExercise = pageData.budgetaryExercises.find(
            (be) => be.id === budget.budgetaryExerciseId,
          );

          if (!budgetaryExercise) {
            return null;
          }

          return (
            <BudgetOverviewPageContent
              budgetaryExercise={budgetaryExercise}
              budgetId={budgetId}
              onRangeChange={setSelectedPeriodRange}
              periodRange={selectedPeriodRange}
              expenseCategory={{
                id: expenseCategoryId,
                name:
                  expenseCategoriesBreakdown.expenseCategories.find(
                    (ec) => ec.id === expenseCategoryId,
                  )?.name ?? '',
                isOther: false,
              }}
            />
          );
        }}
      </QuerySuspense>
    </ErrorBoundary>
  );
};
